import React, {
    useRef,
    useState,
    forwardRef,
    useImperativeHandle,
    useEffect
} from "react";
import { Button } from "primereact/button";
import classes from "./SingleFileUpload.module.css";
import {showError} from "../../../api/notification/ToastManager";

const SingleFileUpload = forwardRef((
    {
        buttonLabel = "",
        onFileChange,
        accept = "*",
        initialFile = null,
        disabled = false,
        disableDeleteButton = false
    },
    ref
) => {
    const fileInputRef = useRef(null);

    const [selectedFile, setSelectedFile] = useState(initialFile);
    const allowedExtensions = accept === "*" ? [] : accept.split(",").map(ext => ext.trim());

    useEffect(() => {
        if (!selectedFile && initialFile) {
            setSelectedFile(initialFile);
            if (onFileChange) onFileChange(initialFile,false);
        }
    }, [initialFile]);

    useImperativeHandle(ref, () => ({
        getFile: () => selectedFile || null,
        clearFile: () => {
            setSelectedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
            if (onFileChange) onFileChange(null);
        }
    }));

    const handleButtonClick = (e) => {
        e.preventDefault();
        fileInputRef.current.click();
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            const name = file.name;
            const dotIndex = name.lastIndexOf(".");
            const extension = dotIndex !== -1 ? name.substring(dotIndex) : "";

            if (accept === "*" || allowedExtensions.includes(extension)) {
                setSelectedFile(file);
                if (onFileChange) onFileChange(file, true);
            } else {
                showError(
                    "Unsupported file extension",
                    `Allowed extensions: ${allowedExtensions.join(", ")}`
                );
                if (fileInputRef.current) fileInputRef.current.value = "";
                setSelectedFile(null);
                if (onFileChange) onFileChange(null, false);
            }
        }
        e.target.value=null;
    };

    const handleClear = (e) => {
        e.preventDefault();
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (onFileChange) onFileChange(null,true);
    };

    const truncateFileName = (name) => {
        if (name.length > 24) {
            return `${name.substring(0, 10)}...${name.substring(name.length - 10)}`;
        }
        return name;
    };

    const handleDownload = (e) => {
        e.preventDefault();
        const file = selectedFile;
        if (!file) return;

        const url = URL.createObjectURL(file);
        const a = document.createElement("a");
        a.href = url;
        a.download = file.name || "download";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const displayedFileName = selectedFile?.name || "";

    return (
        <div className="mb-4">
            <div className="flex items-center gap-2">
                <input
                    type="file"
                    ref={fileInputRef}
                    accept={accept}
                    onChange={handleFileChange}
                    className="hidden"
                />
                <Button
                    icon="pi pi-upload"
                    disabled={disabled}
                    label={displayedFileName ? truncateFileName(displayedFileName) : buttonLabel}
                    onClick={handleButtonClick}
                    className={`p-button-md actionBtn ${classes.actionBtn} ${displayedFileName ? "has-file" : ""}`}
                    aria-label={displayedFileName ? "Change file" : "Select file"}
                    type="button"
                />
                {selectedFile && (
                    <div className="flex gap-1">
                        <Button
                            disabled={disableDeleteButton}
                            icon="pi pi-download"
                            className="p-button-rounded-sm p-button-text p-button p-component p-button-icon-only"
                            style={{
                                height: '0.5rem',
                                width: '1.5rem',
                                color: '#487c84',
                            }}
                            onClick={handleDownload}
                            aria-label="Download file"
                            type="button"
                        />
                        <Button
                            disabled={disableDeleteButton}
                            icon="pi pi-trash"
                            className="p-button-rounded-sm p-button-text p-button p-component p-button-icon-only"
                            style={{
                                height: '0.5rem',
                                width: '1.5rem',
                                color: '#487c84',
                            }}
                            onClick={handleClear}
                            aria-label="Remove file"
                            type="button"
                        />
                    </div>
                )}
            </div>
        </div>
    );
});

export default SingleFileUpload;